/**
 * Bridge the embedded Custio dashboard to the main demo backend (bot session).
 * Parent meeting UI or URL can supply `demoSessionId` / `sessionId` (bare uuid or `demo-<uuid>`).
 */

const STORAGE_KEY = 'demo_bot_session_id';

function normalizeSessionId(raw) {
  return String(raw || '').replace(/^demo-/i, '').trim();
}

function mainBackendBaseUrl() {
  const raw = (
    process.env.REACT_APP_MAIN_BACKEND_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    ''
  ).trim();
  const fallback = 'http://localhost:5000';
  const env = raw || fallback;

  if (typeof window === 'undefined') {
    return env.replace(/\/+$/, '');
  }

  const host = window.location.hostname;
  const pageIsLoopback = host === 'localhost' || host === '127.0.0.1';

  try {
    const normalized = env.includes('://') ? env : `https://${env}`;
    const apiUrl = new URL(normalized);
    const apiHost = apiUrl.hostname.toLowerCase();
    if (apiHost === host.toLowerCase()) {
      return env.replace(/\/+$/, '');
    }
    const httpsPageHttpApi =
      window.location.protocol === 'https:' && apiUrl.protocol === 'http:';
    const apiIsLoopback = apiHost === 'localhost' || apiHost === '127.0.0.1';
    if (httpsPageHttpApi || (apiIsLoopback && !pageIsLoopback)) {
      return window.location.origin.replace(/\/+$/, '');
    }
    return env.replace(/\/+$/, '');
  } catch {
    return env.replace(/\/+$/, '');
  }
}

export function getBotDemoSessionId() {
  try {
    const fromStorage = sessionStorage.getItem(STORAGE_KEY);
    if (fromStorage) return normalizeSessionId(fromStorage);
  } catch (_) { /* ignore */ }
  if (typeof window === 'undefined') return '';
  const sp = new URLSearchParams(window.location.search);
  const q = (sp.get('demoSessionId') || sp.get('sessionId') || '').trim();
  return normalizeSessionId(q);
}

export function persistBotDemoSessionId(raw) {
  const sid = normalizeSessionId(raw);
  if (!sid) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, sid);
  } catch (_) { /* ignore */ }
}

function postJson(path, payload) {
  const sessionId = normalizeSessionId(payload.sessionId);
  if (!sessionId) return;
  const url = `${mainBackendBaseUrl()}${path}`;
  const body = JSON.stringify({ ...payload, sessionId });
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch (_) { /* fall through */ }
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    mode: 'cors',
  }).catch(() => {});
}

/** User clicked sidebar (or parent synced route) — bot polls GET /api/bot/user-navigated. */
export function reportDashboardUserNavigated(section) {
  const sessionId = getBotDemoSessionId();
  if (!sessionId || !section) return;
  postJson('/api/bot/user-navigated', { sessionId, section });
}

/** Route finished rendering — bot GET /api/bot/page-loaded matches `section`. */
export function reportDashboardPageLoaded(section) {
  const sessionId = getBotDemoSessionId();
  if (!sessionId) return;
  postJson('/api/bot/page-loaded', { sessionId, section: section || '' });
}
